<template>
  <div class="container-xxl py-25">
    <MetaTags
      :title="`Вакансия ${vacancy.profession.title} г. ${vacancy.city.title}: ${vacancy.company.name} приглашает кандидатов на видео-платформу`"
      :description="`Узнайте больше о вакансии ${vacancy.profession.title} в ${vacancy.company.name}. Посмотрите видео и подайте заявку прямо сейчас!`"
    />
    <AppBreadcrumbs
      :links="[
        {
          text: `Вакансии ${vacancy.city.titleIn ?? 'в ' + vacancy.city.title}`,
          alias: `/${vacancy.city.alias}/vacancies`
        },
        {
          text: vacancy.profession.title,
          alias: `/${vacancy.city.alias}/vacancies/${vacancy.profession.alias}`
        },
        {
          text: vacancy.company.name,
        }
      ]"
    />
    <div class="row">
      <div class="col-12 col-lg-9">
        <div class="row">
          <div class="col-12 col-md-5 col-lg-4">
            <VideoPlayer
              id="videoPlayer"
              class="rounded-20 overflow-hidden"
              :answers="getAnswers"
              :only-step="true"
            />
          </div>
          <div class="col-12 col-md-7 col-lg-8 mt-10 mt-md-0">
            <WindowItem>
              <div
                v-if="vacancy.isPublished"
                class="d-flex gap-24 justify-content-center"
              >
                <AppButton
                  class="btn-book"
                  :class="vacancy.favorite === null ? 'border booked' : null"
                  title="Добавить в избранное"
                  :active="vacancy.favorite === null"
                  is-icon
                  @click="favoriteControl"
                >
                  <svg class="h-100 w-100">
                    <use xlink:href="/public/icons/main.svg#heart-fill" />
                  </svg>
                </AppButton>
                <AppButton
                  class="rounded-10"
                  :class="vacancy.response !== null ? 'btn-book' : null"
                  :variant="vacancy.response === null ? BtnVariant.Primary : null"
                  @click="respond"
                >
                  {{ vacancy.response !== null ? 'Отмена' : 'Откликнуться' }}
                </AppButton>
              </div>
              <div
                v-else
                class="h1 text-danger"
              >
                Вакансия снята с публикации
              </div>
              <h1
                class="heading-section-3"
                :class="{'text-gray': !vacancy.isPublished}"
              >
                {{ vacancy.title }}
              </h1>
              <div
                v-if="vacancy.isPublished"
                class="mb-20"
              >
                <div class="fw-bold">
                  Контакты
                </div>
                <div
                  v-if="getPhone !== undefined"
                  class="mt-5"
                >
                  <a
                    class="text-decoration-none text-primary"
                    :href="'mailto:' + getPhone"
                  >
                    {{ getPhone }}
                  </a>
                </div>
                <div
                  v-if="getSocial.email !== undefined"
                  class="mt-5"
                >
                  <a
                    class="text-decoration-none text-primary"
                    :href="'tel:' + getSocial.email"
                  >
                    {{ getSocial.email }}
                  </a>
                </div>
                <div class="d-flex flex-wrap mt-5">
                  <a
                    v-if="getSocial.whatsapp !== undefined"
                    class="d-flex me-20 align-items-center text-decoration-none text-black"
                    title="Whatsapp"
                    :href="`https://wa.me/${getSocial.whatsapp}`"
                    target="_blank"
                  >
                    <svg
                      height="25"
                      width="25"
                      class="me-5"
                    >
                      <use xlink:href="/public/icons/whatsapp.svg#whatsapp" />
                    </svg>
                    <span>Whatsapp</span>
                  </a>
                  <a
                    v-if="getSocial.telegram !== undefined"
                    class="d-flex me-20 align-items-center text-decoration-none text-black"
                    title="Telegram"
                    :href="`https://t.me/+${getSocial.telegram}`"
                    target="_blank"
                  >
                    <svg
                      height="25"
                      width="25"
                      class="me-5"
                    >
                      <use xlink:href="/public/icons/telegram-blue.svg#telegram-blue" />
                    </svg>
                    <span>Telegram</span>
                  </a>
                  <a
                    v-if="getSocial.viber !== undefined"
                    class="d-flex align-items-center text-decoration-none text-black"
                    title="Должно быть установлено приложение Viber"
                    :href="`viber://chat?number=+${getSocial.viber}`"
                    target="_blank"
                  >
                    <svg
                      height="25"
                      width="25"
                      class="me-5"
                    >
                      <use xlink:href="/public/icons/viber.svg#viber" />
                    </svg>
                    <span>Viber</span>
                  </a>
                </div>
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Зарплата
                </div>
                <span class="fw-bold text-primary">{{ getSalary() }}</span>
              </div>
              <div
                v-if="vacancy.address !== null"
                class="mb-20"
              >
                <div class="fw-bold">
                  Адрес
                </div>
                {{ vacancy.address }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Опыт работы в данной должности
                </div>
                {{ vacancy.experience.title }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  Тип занятости
                </div>
                {{ vacancy.typeEmployment.name }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  График работы
                </div>
                {{ getScheduleList }}
              </div>
              <div class="mb-20">
                <div class="fw-bold">
                  О вакансии
                </div>
                {{ vacancy.description }}
              </div>
              <div class="d-flex gap-24 justify-content-center align-items-center">
                <span>
                  Опубликовано:
                  {{ new Date(vacancy.publicationDate * 1000).toLocaleString(['ru-RU', 'en-US'], {day: '2-digit', month: '2-digit', year: 'numeric'}) }}
                </span>
              </div>
            </WindowItem>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-3 mt-10 mt-lg-0">
        <div class="d-none d-flex gap-25 justify-content-center bg-white rounded-20 px-20 py-34 mb-10">
          <button
            class="btn btn-book"
            :class="vacancy.favorite === null ? 'booked' : null"
            @click="favoriteControl"
          >
            F
          </button>
          <button
            class="btn btn-primary"
            @click="respond"
          >
            Откликнуться
          </button>
        </div>
        <div class="d-none bg-white rounded-20 px-20 py-34 mb-10 text-center">
          <button class="btn btn-white shadow-1 btn-circle me-24">
            L
          </button>
          <button class="btn btn-white shadow-1 btn-circle">
            R
          </button>
        </div>
        <div class="d-none bg-white rounded-20 px-20 py-34 mb-10 text-center">
          Опубликовано:
          {{ (new Date(vacancy.publicationDate * 1000)).toLocaleDateString(['ru-RU', 'en-US'], {day: '2-digit', month: 'long', year: 'numeric'}) }}
        </div>
        <CompanySearchItem
          :id="company.id"
          :title="company.name"
          :img="company.logo"
          :time="new Date(company.created * 1000)"
          :count-vacancy="company.countVacancy"
          :type-company="company.type === 1 ? 'Работодатель' : 'Агенство'"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Vacancy } from "~/entities/vacancy";
import type { Company } from "~/entities/company";
import { useClient } from "~/stores/client";
import { useApiFavorite } from "~/composables/api/favorite";
import {useModal} from "vue-final-modal";
import {BtnVariant} from "~/composables/enums/BtnVariant";
import AuthorizationModal from "~/components/modal/AuthorizationModal.vue";
import { useApiResponses } from "~/composables/api/responses";
import {usePhoneNormalize} from "~/composables/utils/phoneNormalize";

const { $toast } = useNuxtApp();
const route = useRoute();
const client = useClient();
let favorite = useApiFavorite(client.getClientId!);
const apiResponses = useApiResponses();

const useAuthorizationModal = useModal({
  component: AuthorizationModal,
  attrs: {
    onConfirm() {
      useAuthorizationModal.close();
    },
    onLogin() {
      favorite = useApiFavorite(client.getClientId);
      getVacancy();
    }
  }
});

//-----STATE-----\\
const vacancy = ref<Vacancy & {favorite: {id: string}|null}>();
const company = ref<Company & {countVacancy: number}>();

//-----COMPUTED-----\\
const getAnswers = computed((): Answer[] => {
  // return vacancy.value!.questions.filter(item => item.answer !== null).map((question: any): Answer => ({
  return vacancy.value!.questions.map((question: any): Answer => ({
    id: question.id,
    question: question.question,
    src: question.answer?.src??null,
    created: question.created,
    duration: question.answer.duration
  }));
});
const getSocial = computed<Object[]>(() => {
  if (vacancy.value.social !== null && Object.keys(vacancy.value.social).length > 0) {
    return vacancy.value.social;
  }

  return [];
});
const getPhone = computed(() => {
  if (getSocial.value.phone === undefined) {
    return undefined;
  }

  return usePhoneNormalize(getSocial.value.phone);
});

//-----ASYNC-----\\
await getVacancy();
const companyData = await mainFetch(`companies/${vacancy.value.company.id}`);
company.value = companyData.data.value.company;

//-----METHODS-----\\
function getSalary(): string {
  if (vacancy.value.salary.min === null && vacancy.value.salary.max === null) {
    return 'з/п не указана';
  }

  let tmp: string = '';
  if (vacancy.value.salary.min !== null) {
    tmp += 'от ' + vacancy.value.salary.min.toLocaleString() + ' ';
  }
  if (vacancy.value.salary.max !== null) {
    tmp += 'до ' + vacancy.value.salary.max.toLocaleString();
  }
  tmp += ' руб.';
  return tmp;
}
async function getVacancy() {
  const { data, error } = await mainFetch(`vacancies/${route.params.vacancy_id}`,{
    query: {
      'get[questions]': true,
      'get[getResponse]': true
    }
  });
  if (error.value !== null) {
    throw createError({
      statusCode: error.value?.data.error.code,
      statusMessage: error.value?.data.error.clientMessage,
    });
  }

  vacancy.value = data.value.vacancy;
}

const getScheduleList = computed<string>(() => {
  if (vacancy.value.schedules.length === 0) {
    return '';
  }

  let response = '';
  for (let i = 0; i < vacancy.value.schedules.length; i++) {
    response += (i === 0 ? '' : ', ') + vacancy.value.schedules[i].name;
  }

  return response;
});

async function favoriteControl() {
  if (!client.isAuthenticated) {
    useAuthorizationModal.open();
    return;
  }

  if (vacancy.value.favorite === null) {
    const id: string|null = await favorite.add(route.params.vacancy_id as string, 1);
    if (id !== null) {
      vacancy.value.favorite = { id };
    }
    return;
  }

  const isAdded: boolean = await favorite.delete(vacancy.value.favorite.id);
  if (isAdded) {
    vacancy.value.favorite = null;
  }
}
async function respond() {
  if (!client.isAuthenticated) {
    useAuthorizationModal.open();
    return;
  }

  if (client.getActiveCompany?.id !== undefined) {
    $toast.error('Компания не может откликаться на вакансии');
    return;
  }

  if (!client.isSummaryCreated) {
    $toast.error('У вас не создано видеорезюме');
    return;
  }

  if (vacancy.value.response !== null) {
    apiResponses.cancel(route.params.vacancy_id as string);

    vacancy.value.response = null;
  } else {
    apiResponses.add(route.params.vacancy_id as string).then((response) => {
      response = response.data.value??response.error.value?.data;
      if (response.error.code !== 200) {
        $toast.error(data.error.clientMessage ?? data.error.message);
        return;
      }

      vacancy.value.response = { id: response.response.id }
    });
  }
}
</script>

<style scoped lang="scss">
.company-avatar {
  height: 80px;
  width: 80px;
}

@media (max-width: 767px) {
  #videoPlayer {
    height: 90vh !important;
  }
}
</style>
